import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ethers } from "ethers";
import ActivityTable from "../components/ActivityTable";
import CardInfo from "../components/CardInfo";
import { EMETH_TOKEN_CONTRACT_ADDRESS, POLYGON_CHAINID } from "../constants";
import ERC20_ABI from "../constants/abi/erc20.json";
import { AppContext } from "../context/AppContext";
import {
  getBalanceOf,
  getCurrentSlot,
  getDeposits,
  getMinTarget,
} from "../utils/contract-utils";
import { Activity } from "../utils/hooks/mining";

import { fixedNumber } from "../utils/number";
import DepositModal from "../components/DepositModal";
import { relay } from "../services/api";

export default function Homepage() {
  const { accountData, account } = useContext(AppContext);
  const [emethTokenBalance, setEmethTokenBalance] = useState();
  const [currentSlot, setCurrentSlot] = useState();
  const [difficulty, setDifficulty] = useState<string>("");
  const [status, setStatus] = useState<boolean>(false);
  const [deposits, setDeposits] = useState("");
  const [rate, setRate] = useState(0);

  const [activities, setActivities] = useState<Activity[]>([]);

  const [showModal, setShowModal] = useState(false);

  // const output =
  //   "fc9f953c1fa52c1d3b663b3f7f7148627f6b9d1745ed1869338d992884843dc86bf05ab30897cdf8ca3a4c6d57fe452621a38914a2fd43b5e20aeb457abc4f271c00000000000000000000000000000000000000000000000000000000063e7a4f";

  // const hashxxxx = sha256(
  //   encHex.parse(
  //     "fc9f953c1fa52c1d3b663b3f7f7148627f6b9d1745ed1869338d992884843dc86bf05ab30897cdf8ca3a4c6d57fe452621a38914a2fd43b5e20aeb457abc4f271c00000000000000000000000000000000000000000000000000000000063e7a4f"
  //   )
  // ).toString();

  // console.log("hashxxxx", hashxxxx);

  // const data =
  //   "fc9f953c1fa52c1d3b663b3f7f7148627f6b9d1745ed1869338d992884843dc86bf05ab30897cdf8ca3a4c6d57fe452621a38914a2fd43b5e20aeb457abc4f271c000000ffffffffffffffffffffffffffffffffffffffffffffffffffffffffff";

  // const minTarget = parseInt(data.substring(130), 16);

  // console.log("number", minTarget);

  const worker = useMemo(() => {
    if (status) {
      return new Worker(new URL("../utils/mineThread.js", import.meta.url));
    }
    return;
  }, [status]);

  const handleStart = useCallback(() => {
    setStatus(true);
  }, []);

  const handleStop = useCallback(() => {
    if (!worker) {
      return;
    }
    setStatus(false);
    worker.terminate();
  }, [worker]);

  useEffect(() => {
    if (!account) {
      return;
    }
    if (window.Worker && worker && status) {
      worker.postMessage("run");
      worker.onmessage = function (e) {
        switch (e.data.type) {
          case "rate":
            const rate = e.data?.data ?? 0;
            setRate(rate);
            break;
          case "result":
            const newData = activities.concat([e.data.data]);
            setActivities(Array.from(new Set(newData)));
            const payload = {
              jobId: e.data.id,
              node: account,
              result: e.data.result,
            };
            try {
              relay(payload);
            } catch (error) {
              console.log("relay error", error);
            }
            break;
        }
      };
    }
  }, [account, activities, status, worker]);

  useEffect(() => {
    getBalanceOf(
      EMETH_TOKEN_CONTRACT_ADDRESS,
      accountData.address,
      ERC20_ABI,
      accountData.provider,
    )
      .then((result) => {
        setEmethTokenBalance(result);
      })
      .catch((error) => {
        console.log("error", error);
      });
  }, [accountData.address, accountData.provider]);

  useEffect(() => {
    getDeposits(accountData.address, accountData.provider)
      .then((result) => {
        setDeposits(ethers.formatUnits(result, 18).toString());
      })
      .catch((error) => {
        console.log("error", error);
      });
  }, [accountData.address, accountData.provider]);

  useEffect(() => {
    getCurrentSlot(accountData.provider)
      .then((result) => {
        setCurrentSlot(result);
      })
      .catch((error) => {
        console.log("error", error);
      });
  }, [accountData, accountData.provider]);

  useEffect(() => {
    const nf = new Intl.NumberFormat();
    getMinTarget(404, accountData.provider)
      .then((result) => {
        if (result > 0) {
          const value = (2 ** 256 - 1) / result;
          setDifficulty(nf.format(value));
        }
      })
      .catch((error) => {
        console.log("error", error);
      });
  }, [accountData.provider]);

  const polygonNetwork = useMemo(() => {
    if (!accountData.address) {
      return "Not Connected";
    }
    if (accountData.chainId === POLYGON_CHAINID) {
      return "Polygon PoS";
    }
    return "Wrong Network";
  }, [accountData.address, accountData.chainId]);

  const currentSlotValue = useMemo(() => {
    if (!accountData.address) {
      return "Not Connected";
    }
    if (accountData.chainId !== POLYGON_CHAINID) {
      return "Wrong Network";
    }
    return currentSlot;
  }, [accountData.address, accountData.chainId, currentSlot]);

  const difficultyValue = useMemo(() => {
    if (!accountData.address) {
      return "Not Connected";
    }
    if (accountData.chainId !== POLYGON_CHAINID) {
      return "Wrong Network";
    }
    return difficulty.toString();
  }, [accountData.address, accountData.chainId, difficulty]);

  const renderTable = useMemo(() => {
    if (activities) {
      return (
        <div className="mt-8 px-6">
          <p className="font-bold text-lg text-white pb-4">Activity</p>
          <div className="flex flex-col justify-center items-center bg-white pb-8">
            <ActivityTable status={status} tableData={activities} />
          </div>
        </div>
      );
    }
    return;
  }, [activities, status]);

  const emethBalance = useMemo(() => {
    if (!emethTokenBalance) {
      return 0;
    }
    const value = parseFloat(emethTokenBalance) / Math.pow(10, 18);
    return fixedNumber(value);
  }, [emethTokenBalance]);

  return (
    <div className="max-w-4xl w-full mx-auto h-96">
      <DepositModal showModal={showModal} setShowModal={setShowModal} />
      <div className="p-6 grid grid-cols-2 gap-4">
        <CardInfo
          account={accountData.address}
          title="MATIC Deposit"
          value={`${fixedNumber(parseFloat(deposits), 4)} MATIC`}
          handleDeposit={() => setShowModal(true)}
        />
        <CardInfo
          account={accountData.address}
          title="EMETH Balance"
          value={`${emethBalance} EMETH`}
        />
        <CardInfo
          account={accountData.address}
          title="Status"
          status={status}
          handleStart={handleStart}
          handleStop={handleStop}
          value={status ? "Running" : "Stopped"}
          rate={rate}
        />
        <CardInfo
          account={accountData.address}
          title="Network"
          value={polygonNetwork}
        />
        <CardInfo
          account={accountData.address}
          title="Current Slot"
          value={currentSlotValue ? currentSlotValue : ""}
        />
        <CardInfo
          account={accountData.address}
          title="Difficulty"
          value={difficultyValue ? difficultyValue : ""}
        />
      </div>
      {/* {renderTable} */}
    </div>
  );
}
